import React from "react";
import "./MembershipJoin.css";
import { formatPriceAmount, normalizeMembershipPricing } from "../../utils/membershipPricing";

// The join screen people land on from the Momentum sales page (/site/momentum).
// It reuses the sales page's "next 3 challenges" block and "What's included"
// styling. Checkout itself is handled by MembershipPage through onCheckout.

// Same calendar as sales-pages/src/components/momentum/content.ts (MONTHS).
const MONTHS = [
  { month: "January", name: "60° North Run", logo: "01-jan", premium: true },
  { month: "February", name: "It’s Still Winter", logo: "02-feb" },
  { month: "March", name: "Slush & Sweat", logo: "03-mar" },
  { month: "April", name: "Canada Blooms Run", logo: "04-apr", premium: true },
  { month: "May", name: "Chasing Feathers", logo: "05-may" },
  { month: "June", name: "The Social Spark", logo: "06-jun" },
  { month: "July", name: "Canada Lakes Run", logo: "07-jul", premium: true },
  { month: "August", name: "Nature Calls", logo: "08-aug" },
  { month: "September", name: "Find Your Rhythm", logo: "09-sep" },
  { month: "October", name: "Joy of Fall Run", logo: "10-oct", premium: true },
  { month: "November", name: "Milestones & Masterpieces", logo: "11-nov" },
  { month: "December", name: "A December to Remember", logo: "12-dec" },
];

// The month a new member starts with (0 = January), kept in step with
// START_MONTH in the sales page's content.ts. 10 = November.
const START_MONTH = 10;

const TRUST = [
  { icon: "lock", label: "Secure, encrypted checkout" },
  { icon: "cancel", label: "Cancel anytime" },
  { icon: "shield", label: "Your information stays private" },
];

const EXTRAS = [
  {
    icon: "plans",
    title: "Running training plans",
    body: "5K and beginner half-marathon plans, plus a pace calculator.",
  },
  {
    icon: "community",
    title: "Supportive community",
    body: "Share your wins and get cheered on by members across Canada.",
  },
  {
    icon: "strength",
    title: "Strong & Healthy hub",
    body: "Strength and mobility programs for long-term health.",
  },
  {
    icon: "meetups",
    title: "Monthly meetups",
    body: "Live member meetups, with recordings and show notes.",
  },
];

export function getNextChallenges(startMonth = START_MONTH) {
  return [0, 1, 2].map((offset) => MONTHS[(startMonth + offset) % 12]);
}

export default function MembershipJoin({ priceList, pricing, couponCode, onCheckout }) {
  const nextChallenges = getNextChallenges();
  // Shown price comes from options/membershipPricing (see utils/membershipPricing).
  const price = pricing || normalizeMembershipPricing(null);
  const amount = formatPriceAmount(price.amount);
  const checkoutButtons = (className) => (
    <div className={`mj-actions ${className || ""}`}>
      {priceList && priceList.map((product) => (
        <button
          key={product.id}
          type="button"
          className="mj-btn"
          onClick={() => onCheckout(product.price.id)}
        >
          Proceed to checkout <ArrowIcon />
        </button>
      ))}
    </div>
  );

  return (
    <div className="mj">
      <header className="mj-head">
        <h1 className="mj-display">Join Momentum today</h1>
        <p>The all-access membership for motivation, challenges, fitness and health.</p>
      </header>

      <section className="mj-card" aria-label="Your Momentum membership">
        <div className="mj-buy">
          <p className="mj-label">Momentum Membership</p>
          <div className="mj-price">
            <b className="mj-display">${amount}</b>
            <span>
              {price.currency} / {price.period}
              {price.was && (
                <>
                  <br />
                  <s>${formatPriceAmount(price.was)}</s> <em>Save {price.discount}</em>
                </>
              )}
            </span>
          </div>
          {couponCode && (
            <p className="mj-coupon">
              Coupon <b>{couponCode}</b> will be applied at checkout.
            </p>
          )}
          {checkoutButtons()}
          <ul className="mj-trust">
            {TRUST.map((item) => (
              <li key={item.icon}>
                <TrustIcon name={item.icon} />
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mj-right">
          <p className="mj-label">Your next 3 challenges</p>
          <ol className="mj-list">
            {nextChallenges.map((challenge) => (
              <li key={challenge.logo} className={challenge.premium ? "premium" : undefined}>
                <span className="mj-step">{challenge.month}</span>
                <span className="mj-tile">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/momentum-logos/${challenge.logo}.webp`}
                    alt={`${challenge.name} logo`}
                    width="180"
                    height="180"
                  />
                </span>
                <span className="mj-name">{challenge.name}</span>
                {challenge.premium && <span className="mj-ptag">Premium · Medal + SWAG</span>}
              </li>
            ))}
          </ol>
          <p className="mj-deal">3 challenges for the price of one</p>
        </div>
      </section>

      <section className="mj-extras" aria-labelledby="mj-extras-heading">
        <p className="mj-eyebrow">Also included</p>
        <h2 id="mj-extras-heading" className="mj-display">Everything you need to keep going.</h2>
        <ul className="mj-feats">
          {EXTRAS.map((extra, index) => (
            <li key={extra.icon} className={`mj-feat f${index}`}>
              <span className="mj-ficon"><ExtraIcon name={extra.icon} /></span>
              <h3>{extra.title}</h3>
              <p>{extra.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mj-end">
        <p>
          <b>${amount} {price.currency} / {price.period}</b> · Cancel anytime
        </p>
        {checkoutButtons("mj-actions-center")}
      </section>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17 17 7M8 7h9v9" />
    </svg>
  );
}

function TrustIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {name === "lock" && <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>}
      {name === "cancel" && <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16M9.5 13.5l5 5M14.5 13.5l-5 5" /></>}
      {name === "shield" && <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6l-7-3Z" /><path d="m9 12 2.2 2.2L15 10.5" /></>}
    </svg>
  );
}

function ExtraIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {name === "plans" && <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V3h6v1M9 10h6M9 14h6M9 18h3" /></>}
      {name === "community" && <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />}
      {name === "strength" && <path d="M6 7v10M3 9.5v5M18 7v10M21 9.5v5M6 12h12" />}
      {name === "meetups" && <><rect x="3" y="6" width="13" height="12" rx="2" /><path d="m16 10 5-3v10l-5-3" /></>}
    </svg>
  );
}
